import { useState } from 'react';

import { Navigate, useSearchParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import useEnv from '@zenbusiness/application-commons-env';
import useAsyncEffect from '@zenbusiness/application-commons-hooks/useAsyncEffect';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { AUTH_PATHS, DEFAULT_SEGMENT } from '#client/build-env';
import RedirectExternal from '#client/components/RedirectExternal';
import * as Routes from '#client/constants/routes';

// The error code expected when someone has an invalid token.
const INVALID_TOKEN_ERROR_CODE = 'invalid_token';
// The error description expected when a user is blocked.
const USER_BLOCKED_ERROR_DESCRIPTION = 'user is blocked';

const Callback = () => {
  const { DEFAULT_ROUTE } = useEnv();
  const { callback } = useAuthentication();
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState();
  const [exception, setException] = useState(null);
  const [searchParams] = useSearchParams();

  const handleLoginCompleted = useClickstreamCallback('loginCompleted', null, { pick: [] });
  const handleUserIdentified = useClickstreamCallback('userIdentified', null, {
    pick: ['userId_', 'userUuid']
  });

  const { OIDC_LOGIN_ENABLED } = useEnv('root-first'); // use MFE values

  useAsyncEffect(async () => {
    if (!loading) return;

    try {
      const authResult = await callback();
      // Compute where to redirect to, or fall back to the default route
      const redirectToParam = searchParams.get('redirectTo');
      let finalRedirectTo;
      if (OIDC_LOGIN_ENABLED) {
        // If OIDC is enabled, the redirect parameter will be the raw path to redirect to
        if (redirectToParam) {
          finalRedirectTo = redirectToParam;
        } else {
          finalRedirectTo = DEFAULT_ROUTE;
        }
      } else {
        // Otherwise, the auth library will have base64 encoded the URL, so parse it out.
        const decodedRedirectTo = redirectToParam ? atob(redirectToParam) : '';
        if (decodedRedirectTo.startsWith('/')) {
          finalRedirectTo = decodedRedirectTo;
        } else {
          finalRedirectTo = DEFAULT_ROUTE;
        }
      }

      // Send clickstream events for login
      if (authResult.userUuid && authResult.type === 'customer') {
        handleUserIdentified({
          userId_: authResult.userUuid,
          userUuid: authResult.userUuid
        });
        handleLoginCompleted();
      }

      // If a customer erroneously logs in with social auth, log them out and show them an error page
      if (['customer', 'unknown'].includes(authResult.type) && !authResult.userUuid && !authResult.accountUuid) {
        finalRedirectTo = `${Routes.logout}?redirectTo=${Routes.accountNotFound}`;
      }

      setRedirectTo(finalRedirectTo);
      setLoading(false);
    } catch (err) {
      setException(err);
    }
  }, [loading, callback, handleUserIdentified, handleLoginCompleted, OIDC_LOGIN_ENABLED,
    searchParams, DEFAULT_ROUTE]);

  const showLoading = loading || !redirectTo;

  if (exception) {
    if (exception.error === INVALID_TOKEN_ERROR_CODE) {
      return (
        <Navigate to={AUTH_PATHS.loginUri} />
      );
    }
    if (exception.errorDescription === USER_BLOCKED_ERROR_DESCRIPTION) {
      return (
        <Navigate to={Routes.userBlocked} />
      );
    }
  }

  if (exception) throw exception;

  if (showLoading) {
    // Wait until callback processed, then redirect to base page.
    return (
      <PageLoading />
    );
  }

  // If navigating to another app, use an external redirect
  if (!redirectTo.startsWith(DEFAULT_SEGMENT)) {
    return (
      <RedirectExternal href={redirectTo} />
    );
  }

  return (
    <Navigate to={redirectTo} />
  );
};

export default Callback;
